import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function MonitorQuota({ refreshKey = 0 }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";
  const [usage, setUsage] = useState(null);
  const [requests, setRequests] = useState([]);
  const [show, setShow] = useState(false);
  const [amount, setAmount] = useState(5);
  const [reason, setReason] = useState("");
  const [sending, setSending] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get("/plan/usage");
      setUsage(data);
      if (!isAdmin) {
        const response = await api.get("/plan/requests");
        setRequests(response.data);
      }
    } catch { /* dashboard remains available */ }
  };
  useEffect(() => { load(); }, [refreshKey, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps
  const pending = requests.some(r => r.status === "pending");
  const submit = async (e) => {
    e.preventDefault(); setSending(true);
    try {
      await api.post("/plan/requests", { additional_slots: Number(amount), reason });
      toast.success("Request submitted for admin review");
      setShow(false); setReason(""); await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSending(false); }
  };
  if (!usage) return null;
  if (isAdmin) {
    return (
      <section className="om-premium-card p-5 sm:p-6" data-testid="monitor-quota">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-[#536d9f]">Administration overview</p>
            <h2 className="mt-1 text-lg font-semibold text-slate-900">WebApplications monitoring</h2>
            <p className="mt-1 text-sm text-slate-600">
              {usage.monitor_count} monitored {usage.monitor_count === 1 ? "webapp" : "webapps"} · {usage.monitor_limit} webapp capacity
            </p>
          </div>
          <span className="rounded-full border border-blue-100 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700">
            {user.role === "superadmin" ? "Super Admin" : "Admin"}
          </span>
        </div>
        <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-slate-100" role="progressbar" aria-valuenow={usage.monitor_count} aria-valuemin={0} aria-valuemax={usage.monitor_limit || 1} aria-label="Monitoring capacity used">
          <div className="h-full rounded-full bg-[#536d9f]" style={{ width: String(Math.min(100, usage.monitor_limit ? usage.monitor_count / usage.monitor_limit * 100 : 100)) + "%" }} />
        </div>
      </section>
    );
  }
  return <section className="rounded-xl border border-blue-100 bg-blue-50/60 p-5" data-testid="monitor-quota">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div><h2 className="font-semibold text-slate-900">{usage.plan === "free" ? "Free plan" : (usage.plan || "Account")} · WebApplications monitoring</h2>
        <p className="text-sm text-slate-600">{usage.monitor_count} of {usage.monitor_limit} monitors used · 5-minute minimum interval</p></div>
      <button type="button" disabled={pending} onClick={() => setShow(!show)} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
        {pending ? "Extra monitors requested" : "Request more free monitors"}</button>
    </div>
    <div className="mt-3 h-2 overflow-hidden rounded-full bg-blue-100"><div className="h-full rounded-full bg-blue-600" style={{width: `${Math.min(100, usage.monitor_limit ? usage.monitor_count / usage.monitor_limit * 100 : 100)}%`}} /></div>
    {show && !pending && <form onSubmit={submit} className="mt-4 space-y-3">
      <label className="block text-sm font-medium">Additional webapps
        <select className="mt-1 w-full rounded-lg border p-2" value={amount} onChange={e => setAmount(e.target.value)}><option value="5">+5</option><option value="10">+10</option><option value="25">+25</option><option value="50">+50</option></select>
      </label>
      <label className="block text-sm font-medium">Reason for the request
        <textarea required minLength={15} maxLength={1000} value={reason} onChange={e => setReason(e.target.value)} className="mt-1 w-full rounded-lg border p-2" rows={3} placeholder="What webapps do you need to monitor?" />
      </label>
      <button disabled={sending || reason.trim().length < 15} className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-50">{sending ? "Submitting…" : "Submit request"}</button>
    </form>}
    {requests.filter(r => r.status !== "pending").slice(0, 1).map(r => <p key={r.id} className="mt-3 text-xs text-slate-500">Latest decision: {r.status}{r.note ? ` — ${r.note}` : ""}</p>)}
  </section>;
}
